/**
 * Iter273.8 — Public Quickstart catalogue + progress tracking.
 *
 * Companion to the authed `/account/quickstart` (`backend/quickstart.py`,
 * `frontend/src/components/account/QuickStartCard.jsx`). The authed
 * track is persona-driven and persists in Mongo; THIS module is for
 * unauthed Self-Serve visitors who want a guided exploration path
 * before they commit to signing up.
 *
 * Beta Tester Live Doc items addressed (one cohesive deliverable):
 *   #28 — Add Quickstart Guide to Self-Serve
 *   #30 — Expand Quickstart with more knowledge steps
 *   #31 — "Keep going" 10–20 checklist items
 *
 * Design rules
 * ------------
 * - Pure FE — no backend writes for the public visitor (one anon
 *   visitor making 12 POSTs to the backend on every step click is bad
 *   for cost + privacy). Progress lives in `localStorage` under
 *   `qt:public-quickstart:v1`.
 * - Auto-trigger surface flags — when the visitor lands on
 *   `/free-scan`, `/methodology`, `/threat-radar`, etc., we set the
 *   corresponding step to `done` automatically so the checklist reflects
 *   real exploration, not just click-bait.
 * - A signed-in visitor who hits `/quickstart` ALSO sees the public
 *   track (no auth required) — they can use it as a "what else can
 *   I explore?" map. The authed `/account/quickstart` remains the
 *   primary onboarding surface for paid users.
 */

const STORAGE_KEY = "qt:public-quickstart:v1";


/**
 * Step catalogue. 12 items spread across:
 *   - 4 knowledge steps (read methodology + Q-day + tier scale + sample report)
 *   - 4 product-exploration steps (free scan, threat radar, atlas, compare)
 *   - 4 commit steps (newsletter, dev RSS, news radar RSS, pricing)
 *
 * Each step:
 *   id            stable identifier — renaming breaks stored progress.
 *   group         "Learn" | "Explore" | "Subscribe & decide"
 *   title         one-line headline
 *   summary       1-sentence body
 *   est_minutes   best-guess time
 *   to            route the CTA points at
 *   cta           CTA button label
 *   autoOn        optional array of pathnames that auto-tick this step
 *                 when visited (handled by the public Quickstart hook
 *                 mounted at the app shell level).
 *   external      true → renders as `target="_blank"` (RSS feeds, etc.)
 */
export const PUBLIC_QUICKSTART_STEPS = [
  // ────────────────────── Learn ──────────────────────
  {
    id: "pub_methodology",
    group: "Learn",
    title: "Read the scoring methodology",
    summary: "How QorTrace turns a wallet address into a 0–100 exposure score. Two minutes makes the rest of the platform make sense.",
    est_minutes: 2,
    to: "/methodology",
    cta: "Open methodology",
    autoOn: ["/methodology"],
  },
  {
    id: "pub_tier_scale",
    group: "Learn",
    title: "Understand the tier scale",
    summary: "Critical / High / Moderate / PQC-Ready — what each tier means and what to do about it. ↑ higher = more exposed.",
    est_minutes: 1,
    to: "/methodology#tier-scale",
    cta: "View tier rubric",
    autoOn: ["/methodology"],   // same surface covers it
  },
  {
    id: "pub_qday",
    group: "Learn",
    title: "Read about Q-Day + HNDL",
    summary: "Why \"harvest now, decrypt later\" is the threat model and when adversaries break Ed25519/secp256k1.",
    est_minutes: 4,
    to: "/docs/q-day-and-hndl",
    cta: "Open Q-Day brief",
    autoOn: ["/docs/q-day-and-hndl"],
  },
  {
    id: "pub_sample_report",
    group: "Learn",
    title: "Read a sample audit report",
    summary: "A real, anonymised audit so you know what you'll get back. Includes the methodology citation footer.",
    est_minutes: 3,
    to: "/sample-report",
    cta: "Open sample",
    autoOn: ["/sample-report"],
  },

  // ───────────────────── Explore ─────────────────────
  {
    id: "pub_free_scan",
    group: "Explore",
    title: "Run a free PQC scan",
    summary: "Paste any wallet address. Get a 0–100 exposure score plus the pubkey-exposed flag in under 30 seconds.",
    est_minutes: 1,
    to: "/free-scan",
    cta: "Run free scan",
    autoOn: ["/free-scan"],
  },
  {
    id: "pub_threat_radar",
    group: "Explore",
    title: "Watch the live Threat Radar",
    summary: "18 chains, refreshed every 30 seconds — see institutional crypto exposure in real time.",
    est_minutes: 2,
    to: "/threat-radar",
    cta: "Open Threat Radar",
    autoOn: ["/threat-radar"],
  },
  {
    id: "pub_atlas",
    group: "Explore",
    title: "Browse the Atlas",
    summary: "Searchable index of audited wallets, contracts, and treasuries — see who's already PQC-scanning.",
    est_minutes: 2,
    to: "/atlas",
    cta: "Open Atlas",
    autoOn: ["/atlas"],
  },
  {
    id: "pub_compare",
    group: "Explore",
    title: "Compare two scans side-by-side",
    summary: "Drop two wallet/contract IDs in and see the diff between their exposure profiles. Useful for migration planning.",
    est_minutes: 2,
    to: "/compare",
    cta: "Open Compare",
    autoOn: ["/compare"],
  },

  // ────────────────── Subscribe & decide ──────────────
  {
    id: "pub_quantum_brief",
    group: "Subscribe & decide",
    title: "Subscribe to the Quantum Brief",
    summary: "Weekly digest of PQC migration news, harvested-key incidents, and the Q-day countdown.",
    est_minutes: 1,
    to: "/quantum-brief",
    cta: "Subscribe",
    autoOn: ["/quantum-brief"],
  },
  {
    id: "pub_dev_releases_rss",
    group: "Subscribe & decide",
    title: "Subscribe to dev releases (RSS)",
    summary: "Pull QorTrace's release notes into your feed reader. The same feed Krown engineering ships from.",
    est_minutes: 1,
    to: "/api/docs/changelog.rss",
    cta: "Open RSS feed",
    external: true,
  },
  {
    id: "pub_news_radar_rss",
    group: "Subscribe & decide",
    title: "Subscribe to Qelli's News Radar (RSS)",
    summary: "Cyber/crypto/fintech headlines curated by Qelli — straight into Feedly / Inoreader / Slack RSS.",
    est_minutes: 1,
    to: "/api/qelli/news/radar.rss",
    cta: "Open RSS feed",
    external: true,
  },
  {
    id: "pub_pricing",
    group: "Subscribe & decide",
    title: "Pick a plan when you're ready",
    summary: "Free for your first scan. Pro Wallet Watch for continuous monitoring. Audit credits for batch reviews.",
    est_minutes: 2,
    to: "/pricing",
    cta: "Compare plans",
    autoOn: ["/pricing"],
  },
];


/** Read the persisted progress map. Returns `{ [step_id]: { state, ts } }`. */
export function loadProgress() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    // Guard against corrupted storage (e.g. extension scrubbing).
    return typeof parsed === "object" && parsed ? parsed : {};
  } catch {
    return {};
  }
}


/** Persist the full progress map. */
function saveProgress(map) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* swallow — storage may be full, blocked, or privacy-mode */
  }
}


/** Mark a step as `done` (or `skipped`). Idempotent. */
export function markStep(stepId, state = "done") {
  const map = loadProgress();
  // Don't downgrade — a done step stays done even if `markStep(id, "skipped")` is called later.
  if (map[stepId]?.state === "done" && state !== "done") return;
  map[stepId] = { state, ts: new Date().toISOString() };
  saveProgress(map);
}


/** Wipe progress — used by the "Restart guide" button. */
export function resetProgress() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* swallow */
  }
}


/** Auto-tick steps whose `autoOn` matches the current pathname. */
export function autoTickForPath(pathname) {
  if (!pathname) return;
  PUBLIC_QUICKSTART_STEPS.forEach((step) => {
    if (!step.autoOn) return;
    if (step.autoOn.some((p) => pathname === p || pathname.startsWith(p + "/"))) {
      markStep(step.id, "done");
    }
  });
}


/** Summary stats for the progress bar / completion pill. */
export function summarise(progress) {
  const total = PUBLIC_QUICKSTART_STEPS.length;
  const done = PUBLIC_QUICKSTART_STEPS
    .filter((s) => progress[s.id]?.state === "done").length;
  const pct = total === 0 ? 0 : Math.round((done / total) * 100);
  return { total, done, pct };
}


// ────────────────────────── server sync ──────────────────────────────
//
// Iter273.9 — optional account-bound sync of the public Quickstart
// progress. When the visitor is signed in AND has opted in, every
// toggle/auto-tick pushes a small POST to /api/account/public-quickstart/
// sync. On page load (signed-in case), we GET the same endpoint and
// merge the server payload into localStorage so the user sees the
// same checklist on a second device.
//
// The functions below are fire-and-forget — the page UI must NEVER
// block on a sync round-trip. localStorage stays the source of truth
// from the visitor's perspective; the server is an opt-in mirror.

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


/** Pull the current sync state + server-side progress (best-effort). */
export async function fetchServerSync() {
  try {
    const r = await fetch(`${API}/account/public-quickstart`, {
      credentials: "include",
    });
    if (r.status === 401) return null;      // not signed in
    if (!r.ok) return null;
    return await r.json();
  } catch {
    return null;
  }
}


/** Merge the server payload into local state. Returns the combined map. */
export function mergeServer(local, server) {
  const merged = { ...(local || {}) };
  for (const [stepId, ent] of Object.entries(server?.progress || {})) {
    if (!ent || typeof ent !== "object") continue;
    const cur = merged[stepId];
    // Later timestamp wins. Same rule as the server-side merge.
    if (!cur) { merged[stepId] = ent; continue; }
    const curTs = cur.ts || "";
    const newTs = ent.ts || "";
    if (newTs > curTs) merged[stepId] = ent;
  }
  return merged;
}


/** Push the current localStorage map to the server. Best-effort. */
export async function pushServerSync(progress) {
  try {
    await fetch(`${API}/account/public-quickstart/sync`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ progress: progress || {} }),
    });
  } catch {
    /* swallow — sync is non-blocking */
  }
}


/** Flip the opt-in toggle ON (server-side) + push current local map. */
export async function optInServerSync(progress) {
  try {
    const r = await fetch(`${API}/account/public-quickstart/opt-in`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ progress: progress || {} }),
    });
    return r.ok ? await r.json() : null;
  } catch {
    return null;
  }
}


/** Flip the opt-in toggle OFF. Stored progress stays on the server
    so the user can re-enable later. */
export async function optOutServerSync() {
  try {
    const r = await fetch(`${API}/account/public-quickstart/opt-out`, {
      method: "POST",
      credentials: "include",
    });
    return r.ok ? await r.json() : null;
  } catch {
    return null;
  }
}
